import React, { useState } from 'react';
import { Spin } from 'antd';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ExpandableRow } from '@/components/autopilot/expandable-row';
import dayjs from 'dayjs';

interface StageLogData {
  stage: string;
  startedAt: string;
  endedAt: string | null;
  working: number;
  areaTemp: number;
  setTemp: number;
  occupancy: number;
  solar: number;
  duration: number;
  shadow?: boolean;
}

interface EventLogData {
  time: string;
  event: string;
  name: string;
  working: number;
  areaTemp: number;
  setTemp: number;
  occupancy: number;
  solar: number;
  shadow?: boolean;
  stages?: StageLogData[];
  isExpanded?: boolean;
  isOngoing?: boolean;
}

interface EventType {
  id: string;
  label: string;
  name: string;
  color: string;
  icon?: React.ReactNode;
}

interface EventLogTableProps {
  eventLogData: EventLogData[];
  eventTypes: EventType[];
  isLoading: boolean;
  selectedDate: dayjs.Dayjs;
  setSelectedDate: (date: dayjs.Dayjs) => void;
  onRefetch: () => void;
  showDataColumns?: boolean;
}

export const EventLogTable: React.FC<EventLogTableProps> = ({
  eventLogData,
  eventTypes,
  isLoading,
  selectedDate,
  setSelectedDate,
  onRefetch,
  showDataColumns = true
}) => {
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  // Function to toggle the expanded state of a log entry
  const toggleExpand = (index: number) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="flex flex-col min-h-0 flex-1 gap-1.5 pb-2.5 rounded-xl bg-light-grey overflow-hidden">
      {isLoading ? (
        <div className="p-4">
          {/* Table header skeleton */}
          <div className="flex gap-4 h-10 bg-white rounded-lg items-center px-4 mb-3">
            <div className="animate-skeleton h-3 bg-gray-200 rounded w-12"></div>
            <div className="animate-skeleton h-3 bg-gray-200 rounded w-24"></div>
            {showDataColumns && Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="animate-skeleton h-3 bg-gray-200 rounded w-12"></div>
            ))}
          </div>
          
          {/* Table rows skeleton */}
          {Array.from({ length: 8 }).map((_, rowIndex) => (
            <div key={rowIndex} className="flex gap-4 h-12 bg-white rounded-lg items-center px-4 mb-2" 
                 style={{ animationDelay: `${rowIndex * 100}ms` }}>
              <div className="animate-skeleton h-3 bg-gray-200 rounded w-12"></div>
              <div className="animate-skeleton h-3 bg-gray-200 rounded flex-1"></div>
              {showDataColumns && Array.from({ length: 5 }).map((_, colIndex) => (
                <div key={colIndex} className="animate-skeleton h-3 bg-gray-200 rounded w-12"></div>
              ))}
            </div>
          ))}
          
          <div className="flex justify-center items-center mt-6 gap-4">
            <div className="w-24 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-primary progress-flow rounded-full"></div>
            </div>
            <span className="text-sm text-gray-600 font-medium">Loading event log data...</span>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto min-h-0">
          <table className="w-full">
            <colgroup>
              <col className="w-[50px]" />
              <col className="" />
              {showDataColumns && (
                <>
                  <col className="w-[60px]" />
                  <col className="w-[60px]" />
                  <col className="w-[60px]" />
                  <col className="w-[60px]" />
                  <col className="w-[60px]" />
                </>
              )}
            </colgroup>
            <thead className="px-1.5 border-b-2 border-white-blue text-xs text-dark-grey h-12 mb-1.5 sticky top-0 bg-light-grey z-10">
              <tr className="px-1.5">
                <th className="px-2 paragraph-10 text-dark-grey text-center">Time</th>
                <th className="px-2 paragraph-10 text-dark-grey">Event</th>
                {showDataColumns && (
                  <>
                    <th className="px-1 paragraph-10 text-dark-grey text-center">Working %</th>
                    <th className="px-1 paragraph-10 text-dark-grey text-center">Room Temp</th>
                    <th className="px-1 paragraph-10 text-dark-grey text-center">Setpoint</th>
                    <th className="px-1 paragraph-10 text-dark-grey text-center">Occupancy</th>
                    <th className="px-1 paragraph-10 text-dark-grey text-center">Solar Surplus</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="text-xs">
            {(eventLogData || []).length === 0 ? (
              <tr>
                <td colSpan={showDataColumns ? 7 : 2} className="text-center py-4 text-gray-500">
                  No event log data available for the selected date.
                </td>
              </tr>
            ) : (
              (eventLogData || []).map((event, index) => {
                const type = eventTypes.find(v => v.name === event.event);
                const hasStages = event.stages && event.stages.length > 0;
                const isExpanded = expandedRows[index] || false;

                return (
                  <React.Fragment key={index}>
                    <tr
                      className="px-1.5 gap-1 text-black paragraph-9 cursor-pointer hover:bg-white-blue/20 transition-colors duration-150"
                      onClick={() => toggleExpand(index)}
                    >
                      <td className={cn(index === 0 && "pt-3", "text-center align-middle")}>
                        <div className="flex items-center justify-center h-9">
                          {event.isOngoing ? (
                            <span className="paragraph-9 font-medium bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent animate-pulse">
                              Now
                            </span>
                          ) : (
                            <span className="paragraph-9">{event.time}</span>
                          )}
                        </div>
                      </td>
                      <td colSpan={showDataColumns ? 6 : 1} className={cn(index === 0 && "pt-3", "pr-1.5")}>
                        <div className="mb-1 flex items-center bg-white w-full rounded-lg border border-white-blue backdrop-blur-[20px] h-9">
                          <div className="flex items-center px-3 grow">
                            {type && <div className="flex items-center gap-1">
                              <div className={cn("w-4 h-4 rounded flex items-center justify-center", type.color)}>
                                {type.icon}
                              </div>
                              <span className="paragraph-10 text-[#5E5E5E]">{event.name}</span>
                              {event.shadow && (
                                <span className="ml-1.5 text-[9px] px-1 py-0.5 bg-[#F8FAFF] text-gray-500 rounded">shadow</span>
                              )}
                            </div>}
                            {hasStages && (
                              <div className="ml-2">
                                <ChevronRight className={cn("h-3 w-3 text-gray-400 transition-transform duration-150", isExpanded ? "rotate-90" : "")} />
                              </div>
                            )}
                            {/* Add duration for the entire event */}
                            <div className="ml-auto mr-2">
                              <span className="text-[10px] text-dark-grey">
                                {(() => {
                                  // Calculate total duration from all stages
                                  if (event.stages && event.stages.length > 0) {
                                    let totalMinutes = 0;
                                    let hasOngoing = false;
                                    
                                    // Sum up all stage durations
                                    event.stages.forEach(stage => {
                                      if (stage.duration) {
                                        totalMinutes += stage.duration;
                                      } else {
                                        const start = new Date(stage.startedAt);
                                        const end = stage.endedAt ? new Date(stage.endedAt) : new Date();
                                        if (!stage.endedAt) hasOngoing = true;
                                        const durationMs = end.getTime() - start.getTime();
                                        totalMinutes += Math.round(durationMs / (1000 * 60));
                                      }
                                    });
                                    
                                    const hours = Math.floor(totalMinutes / 60);
                                    const mins = totalMinutes % 60;
                                    return hasOngoing 
                                      ? 'Ongoing' 
                                      : hours > 0 
                                        ? `${hours}h ${mins}m` 
                                        : `${mins}m`;
                                  }
                                  return '-';
                                })()}
                              </span>
                            </div>
                          </div>
                          {showDataColumns && (
                            <>
                              <div className="w-[60px] px-2 flex items-center">
                                <div className="w-full h-5 px-1.5 py-[5px] rounded bg-[#F8FAFF]">
                                  <p className="paragraph-10 text-center text-gray-500 leading-[10px]">{event.working % 1 === 0 ? event.working : event.working.toFixed(1)}</p>
                                </div>
                              </div>
                              <div className="w-[60px] px-2 flex items-center">
                                <div className="w-full h-5 px-1.5 py-[5px] rounded bg-[#F8FAFF]">
                                  <p className="paragraph-10 text-center text-gray-500 leading-[10px]">{event.areaTemp % 1 === 0 ? event.areaTemp : event.areaTemp.toFixed(1)}</p>
                                </div>
                              </div>
                              <div className="w-[60px] px-2 flex items-center">
                                <div className="w-full h-5 px-1.5 py-[5px] rounded bg-[#F8FAFF]">
                                  <p className="paragraph-10 text-center text-gray-500 leading-[10px]">{event.setTemp % 1 === 0 ? event.setTemp : event.setTemp.toFixed(1)}</p>
                                </div>
                              </div>
                              <div className="w-[60px] px-2 flex items-center">
                                <div className="w-full h-5 px-1.5 py-[5px] rounded bg-[#F8FAFF]">
                                  <p className="paragraph-10 text-center text-gray-500 leading-[10px]">{event.occupancy % 1 === 0 ? event.occupancy : event.occupancy.toFixed(1)}</p>
                                </div>
                              </div>
                              <div className="w-[60px] px-2 flex items-center">
                                <div className="w-full h-5 px-1.5 py-[5px] rounded bg-[#F8FAFF]">
                                  <p className="paragraph-10 text-center text-gray-500 leading-[10px]">{event.solar % 1 === 0 ? event.solar : event.solar.toFixed(1)}</p>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded stages section */}
                    {hasStages && (
                      <tr>
                        <td colSpan={showDataColumns ? 7 : 2} className="p-0">
                          <ExpandableRow isExpanded={isExpanded}>
                            {event.stages?.map((stage, stageIndex) => (
                              <div key={stageIndex} className="flex bg-white-blue/5">
                                <div className="w-[50px] flex items-center justify-center text-center h-9">
                                  <p className="paragraph-9 text-gray-400">{dayjs(stage.startedAt).format('HH:mm')}</p>
                                </div>
                                <div className="flex-1 pr-1.5">
                                  <div className="mb-1 flex items-center bg-white w-full rounded-lg border border-white-blue backdrop-blur-[20px] h-9 opacity-80">
                                    <div className="flex items-center px-3 grow">
                                      <div className="flex items-center gap-1">
                                        <span className="paragraph-10 text-[#5E5E5E]">Stage: {stage.stage}</span>
                                        {stage.shadow && (
                                          <span className="ml-1.5 text-[9px] px-1 py-0.5 bg-[#F8FAFF] text-gray-500 rounded">shadow</span>
                                        )}
                                      </div>
                                      {/* Add duration for each stage */}
                                      <div className="ml-auto mr-2">
                                        <span className="text-[9px] text-dark-grey">
                                          {(() => {
                                            if (stage.duration) {
                                              // Use the provided duration if available
                                              const hours = Math.floor(stage.duration / 60);
                                              const mins = stage.duration % 60;
                                              return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
                                            } else {
                                              // Calculate duration from timestamps
                                              const start = new Date(stage.startedAt);
                                              const end = stage.endedAt ? new Date(stage.endedAt) : new Date();
                                              const durationMs = end.getTime() - start.getTime();
                                              const minutes = Math.round(durationMs / (1000 * 60));
                                              const hours = Math.floor(minutes / 60);
                                              const mins = minutes % 60;
                                              return !stage.endedAt 
                                                ? 'Ongoing' 
                                                : hours > 0 
                                                  ? `${hours}h ${mins}m` 
                                                  : `${mins}m`;
                                            }
                                          })()}
                                        </span>
                                      </div>
                                    </div>
                                    {showDataColumns && (
                                      <>
                                        <div className="w-[60px] px-2 flex items-center">
                                          <div className="w-full h-5 px-1.5 py-[5px] rounded bg-[#F8FAFF]">
                                            <p className="paragraph-10 text-center text-gray-500 leading-[10px]">{stage.working % 1 === 0 ? stage.working : stage.working.toFixed(1)}</p>
                                          </div>
                                        </div>
                                        <div className="w-[60px] px-2 flex items-center">
                                          <div className="w-full h-5 px-1.5 py-[5px] rounded bg-[#F8FAFF]">
                                            <p className="paragraph-10 text-center text-gray-500 leading-[10px]">{stage.areaTemp % 1 === 0 ? stage.areaTemp : stage.areaTemp.toFixed(1)}</p>
                                          </div>
                                        </div>
                                        <div className="w-[60px] px-2 flex items-center">
                                          <div className="w-full h-5 px-1.5 py-[5px] rounded bg-[#F8FAFF]">
                                            <p className="paragraph-10 text-center text-gray-500 leading-[10px]">{stage.setTemp % 1 === 0 ? stage.setTemp : stage.setTemp.toFixed(1)}</p>
                                          </div>
                                        </div>
                                        <div className="w-[60px] px-2 flex items-center">
                                          <div className="w-full h-5 px-1.5 py-[5px] rounded bg-[#F8FAFF]">
                                            <p className="paragraph-10 text-center text-gray-500 leading-[10px]">{stage.occupancy % 1 === 0 ? stage.occupancy : stage.occupancy.toFixed(1)}</p>
                                          </div>
                                        </div>
                                        <div className="w-[60px] px-2 flex items-center">
                                          <div className="w-full h-5 px-1.5 py-[5px] rounded bg-[#F8FAFF]">
                                            <p className="paragraph-10 text-center text-gray-500 leading-[10px]">{stage.solar % 1 === 0 ? stage.solar : stage.solar.toFixed(1)}</p>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </ExpandableRow>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};
